import { Component, Inject, OnInit, DOCUMENT, ChangeDetectionStrategy } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { MessageService } from "./modules/shared/message/message.service";
import { UserService } from "./services/user/user.service";
import { getUrlParamValue } from "./util/url.util";
import { TranslateService } from "@ngx-translate/core";
import { bootIntercom } from "./util/intercom.util";
import { firstValueFrom } from "rxjs";
import { AltRoutes, appPageAltroutes, canonicalOrigin, categoryPageAltroutes, hreflangUrls } from "./util/page-language.util";
import { syncSessionCookie } from "./util/session-cookie.util";
import { captureAttribution } from "./util/attribution.util";
import { replacePrerenderedSnapshot } from "./util/prerender-snapshot.util";

@Component({
  standalone: false,
  selector: 'app-root',
  // Deliberately Eager (COMBI-4468): this is the root component. Angular only descends into it when it is
  // dirty, so making it OnPush skips the entire tree below it - including every Eager component that relies
  // on being checked whenever an ancestor is checked (notably the config steps).
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {

  constructor(private translate: TranslateService,
              private userService: UserService,
              private router: Router,
              @Inject(DOCUMENT) private dom: Document,
              private messageService: MessageService,
              private activatedRoute: ActivatedRoute,
              private titleService: Title,
              private metaService: Meta) {

    translate.setFallbackLang('en');

    // refresh the session marker cookie, so .htaccess keeps visitors with a session on this host (COMBI-4521)
    syncSessionCookie(!!userService.getUser());

    // remember where this visitor came from, for the installation funnel per channel
    captureAttribution(dom);

    let language = getUrlParamValue(window.location.search, "language");
    if (!language) {
      const locale = getUrlParamValue(window.location.search, "locale");
      // note that if an unknown locale is passed, we fall back to "en" (see "setFallbackLang" above)
      language = locale?.length > 1 ? locale.substring(0, 2).toLowerCase() : undefined;
    }

    if (language) {
      userService.languageOverride = language;
      if (userService.getUser()) {
        userService.setUser(userService.getUser(), false);
      } else {
        translate.use(language);
      }
    } else if (userService.getUser()?.language) {
      if ("de" === userService.getUser().language) {
        // backend "supports" "de", but frontend doesn't, so fall back to "en"
        translate.use("en");
      } else {
        translate.use(userService.getUser().language);
      }
    } else {
      const domain = window.location.host;
      if (domain.startsWith("en.")) {
        translate.use("en");
      } else if (domain.startsWith("nl.")) {
        translate.use("nl");
      } else {
        translate.use(translate.getBrowserLang());
      }
    }

    // update the html lang attribute if it's not the default (en)
    if (translate.getCurrentLang() !== "en") {
      const htmlElement: HTMLHtmlElement = this.dom.querySelector("html");
      htmlElement.setAttribute("lang", translate.getCurrentLang());
    }
    const head = this.dom.getElementsByTagName("head")[0];
    // a pre-rendered snapshot (scripts/prerender.js) already carries the favicon
    if (!this.dom.querySelector("link[rel='shortcut icon']")) {
      const favicon = this.dom.createElement('link') as HTMLLinkElement;
      favicon.setAttribute("rel", "shortcut icon");
      favicon.setAttribute("href", `./assets/img/favicon/combidesk-favicon.png`);
      head.appendChild(favicon);
    }
  }

  async ngOnInit() {
    // a pre-rendered page stays visible until the app has rendered it too (no need to await this)
    replacePrerenderedSnapshot(this.dom, this.router);

    if (!this.titleService.getTitle()) {
      // if no title was set, assume it's not a prerendered page, so add these dynamically
      this.titleService.setTitle(await firstValueFrom(this.translate.get("DEFAULT_PAGE_TITLE")));
      this.metaService.updateTag({
        name: "description",
        content: await firstValueFrom(this.translate.get("DEFAULT_PAGE_META_DESCRIPTION"))
      });
      this.metaService.updateTag({
        name: "keywords",
        content: await firstValueFrom(this.translate.get("DEFAULT_PAGE_META_KEYWORDS"))
      });
    }
    this.metaService.updateTag({ name: "robots", content: "index, follow" });

    this.router.events.subscribe(async (e) => {
      if (e instanceof NavigationEnd) {
        console.log("Navigated to " + e.url + (e.urlAfterRedirects !== e.url ? " (️redirected to " + e.urlAfterRedirects + ")" : ""));
        if (/^\/(koppeling|integration)\//.test(e.url)) {
          // skip, because the CombiPackageDetailsComponent sets the title and SEO tags of these pages itself
        } else {
          // clear any active WARN/ERROR, because those are prolly no longer relevant for the new route
          if (this.messageService.hasActiveStickyMessage()) {
            this.messageService.hideMessage();
          }
          const rt = this.getChild(this.activatedRoute);
          const titleKey = rt.snapshot.data["title"];
          // set the page title, based on what's configured in the routes
          if (titleKey) {
            const title = await firstValueFrom(this.translate.get(titleKey));
            this.titleService.setTitle(title);
            // note: we could also have a different description per route, but using the title for now
            this.metaService.updateTag({
              name: "description",
              content: title
            });
          }

          // set the SEO altroutes if provided
          const altroutes: AltRoutes | undefined = rt.snapshot.data["altroutes"]
              ?? appPageAltroutes(e.urlAfterRedirects)
              ?? categoryPageAltroutes(e.urlAfterRedirects);
          this.updateHreflang(altroutes);
          this.updateCanonical(altroutes);
        }
      }
    });

    // Only booting Intercom on prod/uat by calling the util
    bootIntercom(this.userService, this.translate.getCurrentLang(), this.userService?.getUser());

    setTimeout(() => {
      const successMessageKey = getUrlParamValue(window.location.href, "successMessageKey");
      if (successMessageKey) {
        this.messageService.showSuccessMessage(this.translate.instant(successMessageKey));
      }
      const errorMessageKey = getUrlParamValue(window.location.href, "errorMessageKey");
      if (errorMessageKey) {
        this.messageService.showErrorMessage(this.translate.instant(errorMessageKey));
      }
    }, 500);
  }

  getChild(activatedRoute: ActivatedRoute): ActivatedRoute {
    if (activatedRoute.firstChild) {
      return this.getChild(activatedRoute.firstChild);
    } else {
      return activatedRoute;
    }
  }

  // hreflang links on the canonical hosts, without query parameters. A language without an equivalent page gets no
  // link at all (see hreflangUrls), so an existing one from the previous page is removed.
  private updateHreflang(altroutes?: AltRoutes) {
    const head = this.dom.getElementsByTagName('head')[0];
    const urls = hreflangUrls(window.location.pathname, altroutes);
    for (const lang of ["nl", "en", "x-default"] as const) {
      let element: HTMLLinkElement = this.dom.querySelector(`link[hreflang='${lang}']`) || null;
      if (!urls[lang]) {
        element?.remove();
        continue;
      }
      if (element == null) {
        element = this.dom.createElement('link') as HTMLLinkElement;
        head.appendChild(element);
      }
      element.setAttribute('rel', 'alternate');
      element.setAttribute('hreflang', lang);
      element.setAttribute('href', urls[lang]);
    }
  }

  // rel=”canonical” points to the clean URL without query parameters,
  // so Google treats ?appstore=ExactOnline variants as duplicates of the canonical page.
  private updateCanonical(altroutes?: AltRoutes) {
    const head = this.dom.getElementsByTagName('head')[0];
    let element: HTMLLinkElement = this.dom.querySelector(`link[rel='canonical']`) || null;
    if (element == null) {
      element = this.dom.createElement('link') as HTMLLinkElement;
      head.appendChild(element);
    }
    element.setAttribute('rel', 'canonical');
    const origin = canonicalOrigin(window.location, window.location.pathname, altroutes, this.translate.getCurrentLang());
    element.setAttribute('href', origin + window.location.pathname);
  }
}
